import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  DotsVertical,
  File05,
  ImageIndentLeft,
  LineChartUp02,
  ShieldZap,
  VideoRecorder,
  XClose,
} from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useProduct, useProductRelatedResources } from "@/api/product";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCurrency } from "@/hooks/use-currency";
import { ContentAnalysisTab } from "@/pages/products/tabs/content-analysis-tab";
import { PerformanceTab } from "@/pages/products/tabs/performance-tab";
import { SafetyTab } from "@/pages/products/tabs/safety-tab";
import { VideosTab } from "@/pages/products/tabs/videos-tab";
import { useMerchant } from "@/providers/MerchantProvider";

const tabs = [
  { id: "performance" as const, label: "Performance", icon: <LineChartUp02 className="w-4 h-4" /> },
  { id: "content-analysis" as const, label: "Content Analysis", icon: <ImageIndentLeft className="w-4 h-4" /> },
  { id: "videos" as const, label: "Videos", icon: <VideoRecorder className="w-4 h-4" /> },
  { id: "safety" as const, label: "Safety", icon: <ShieldZap className="w-4 h-4" /> },
];

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  compactDisplay: "short",
  notation: "compact",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

type TabId = "performance" | "content-analysis" | "videos" | "safety";

const Inner = ({
  id,
  onClose,
  tab: tabProp,
  onTabChange,
}: {
  id?: string;
  onClose: () => void;
  tab?: TabId;
  onTabChange?: (tab: TabId) => void;
}) => {
  const [localTab, setLocalTab] = useState<TabId>("performance");
  const { data: product } = useProduct(id);
  const { data: related } = useProductRelatedResources(id);
  const merchant = useMerchant();
  const selectedTab = tabProp ?? localTab;
  const containerRef = useRef<HTMLDivElement>(null);

  const currency = useCurrency();
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    compactDisplay: "short",
  });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [selectedTab]);

  if (!product)
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col overflow-y-auto px-12">
      {/* Header Section - Two Column Layout */}
      <div className="flex gap-6">
        {/* Left Column - Product Image */}
        {product.thumbnail && (
          <img
            src={product.thumbnail}
            alt={product.productName}
            className="w-[216px] h-[250px] rounded-lg object-cover flex-shrink-0"
          />
        )}

        {/* Right Column - Product Info */}
        <div className="flex-1 flex flex-col justify-between min-w-0 h-[250px]">
          {/* Title and Actions Row */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-medium text-primary line-clamp-3">{product.productName}</h2>
              <div className="flex items-center gap-5">
                {product.stockCount > 0 ? (
                  <Badge size="lg" color="success">
                    In stock
                  </Badge>
                ) : (
                  <Badge size="lg" color="error">
                    Out of stock
                  </Badge>
                )}
                <div className="w-2 h-2 bg-utility-gray-300 rounded-full" />
                <a
                  href={`https://shop.tiktok.com/view/product/${product.productId.replace(/^PID-/, "")}?region=GB&locale=en`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-md font-medium text-quaternary underline"
                >
                  See in TikTok shop
                </a>
              </div>
            </div>

            {/* Actions: Dropdown Menu and Close Button */}
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
                    aria-label="More actions"
                  >
                    <DotsVertical className="size-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={5}>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/merchant/$merchantId/smart-briefs"
                      params={{ merchantId: merchant }}
                      search={{ productId: product.productId, productName: product.productName }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <File05 className="w-4 h-4 text-tertiary" />
                      Generate Brief
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/merchant/$merchantId/risks-to-review"
                      params={{ merchantId: merchant }}
                      search={{
                        tab: "pendingManualReview",
                        product: [{ id: product.productId, label: product.productName }],
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <ShieldZap className="w-4 h-4 text-tertiary" />
                      Review Risky Videos
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                type="button"
                onClick={onClose}
                className="flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
                aria-label="Close"
              >
                <XClose className="size-5" />
              </button>
            </div>
          </div>

          {/* Metrics Section - pushed to bottom */}
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-tertiary" />
              <span className="text-sm font-medium text-secondary">Metrics from the last 30 days</span>
            </div>
            <div className="flex items-start w-full">
              <div className="flex flex-col gap-0 flex-1">
                <span className="text-lg font-semibold text-primary">{currencyFormatter.format(product.gmv)}</span>
                <span className="text-sm text-tertiary mb-3">Total GMV</span>
                <TrendText change={product.trendGmv ? (product.gmv - product.trendGmv) / product.trendGmv : null} />
              </div>
              <div className="w-0 self-stretch border-l border-border-primary" />
              <div className="flex flex-col gap-0 flex-1 pl-6">
                <span className="text-lg font-semibold text-primary">{numFormatter.format(product.videos)}</span>
                <span className="text-sm text-tertiary mb-3">Videos</span>
                <TrendText
                  change={product.trendVideos ? (product.videos - product.trendVideos) / product.trendVideos : null}
                />
              </div>
              <div className="w-0 self-stretch border-l border-border-primary" />
              <div className="flex flex-col gap-0 flex-1 pl-6">
                <span className="text-lg font-semibold text-primary">{numFormatter.format(product.unitSales)}</span>
                <span className="text-sm text-tertiary mb-3">Units sales</span>
                <TrendText
                  change={
                    product.trendUnitSales
                      ? (product.unitSales - product.trendUnitSales) / product.trendUnitSales
                      : null
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="mt-14">
        <Tabs
          selectedKey={selectedTab}
          onSelectionChange={(key) => {
            const newTab = key as TabId;
            if (onTabChange) {
              onTabChange(newTab);
            } else {
              setLocalTab(newTab);
            }
          }}
          className="w-full border-b border-border-primary"
        >
          <Tabs.List type="underline" items={tabs} className="w-full before:hidden">
            {(tab) => <Tabs.Item {...tab} className="flex-1" />}
          </Tabs.List>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="flex-1 mt-6">
        {selectedTab === "performance" && (
          <PerformanceTab
            related={related}
            currencyFormatter={currencyFormatter}
            numFormatter={numFormatter}
            productId={product.productId}
            productName={product.productName}
          />
        )}
        {selectedTab === "content-analysis" && (
          <ContentAnalysisTab productId={product.productId} productName={product.productName} />
        )}
        {selectedTab === "videos" && (
          <VideosTab
            related={related}
            currencyFormatter={currencyFormatter}
            numFormatter={numFormatter}
            productId={product.productId}
            productName={product.productName}
          />
        )}
        {selectedTab === "safety" && (
          <SafetyTab
            currencyFormatter={currencyFormatter}
            productId={product.productId}
            productName={product.productName}
          />
        )}
      </div>
    </div>
  );
};

export const ProductDetails = ({
  id,
  onClose,
  tab,
  onTabChange,
}: {
  id?: string;
  onClose: () => void;
  tab?: TabId;
  onTabChange?: (tab: TabId) => void;
}) => {
  if (!id) return null;

  return (
    <Sheet open={!!id} onOpenChange={onClose}>
      <SheetContent className="w-[900px] p-0 py-[48px] sm:max-w-[900px] flex flex-col" hideCloseButton>
        <Inner id={id} onClose={onClose} tab={tab} onTabChange={onTabChange} />
      </SheetContent>
    </Sheet>
  );
};

export const TrendText = ({ change }: { change: number | null }) => {
  if (change === null) {
    return <span className="text-sm text-tertiary">N/A</span>;
  }
  const isPositive = change >= 0;
  return (
    <span className={`text-xs flex items-center gap-0.5 ${isPositive ? "text-success-600" : "text-error-600"}`}>
      {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {percentFormatter.format(Math.abs(change))}
    </span>
  );
};

export const TrendBadge = ({ change }: { change: number | null }) => {
  if (change === null)
    return (
      <Tooltip title="No trend data over the last 30 days">
        <TooltipTrigger className="inline">
          <Badge size="sm" color="gray" className="scale-90 text-xs">
            N/A
          </Badge>
        </TooltipTrigger>
      </Tooltip>
    );
  return (
    <Tooltip title="Change in the last 30 days">
      <TooltipTrigger>
        <Badge size="sm" color={change >= 0 ? "success" : "error"} className="scale-90 text-xs flex">
          {change >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {percentFormatter.format(change)}
        </Badge>
      </TooltipTrigger>
    </Tooltip>
  );
};
